import { useState, useEffect } from 'react'
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { apiClient } from '@/services/apiClient'
import { supabase } from '@/lib/supabase'

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  operatorId: string
}

interface UserCreationConfig {
  cost: number
  bonus: number
}

export default function CreateUserModal({ isOpen, onClose, onSuccess, operatorId }: CreateUserModalProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [initialBalance, setInitialBalance] = useState(0)
  const [createdUser, setCreatedUser] = useState<any>(null)
  const [config, setConfig] = useState<UserCreationConfig>({ cost: 100, bonus: 50 })

  useEffect(() => {
    if (isOpen) {
      fetchConfig()
    }
  }, [isOpen])

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('system_parameters')
        .select('param_value')
        .eq('param_key', 'user_creation_config')
        .single()

      if (data && data.param_value) {
        const parsed = typeof data.param_value === 'string' 
          ? JSON.parse(data.param_value) 
          : data.param_value
        setConfig(parsed)
      }
    } catch (err) {
      console.error('Failed to fetch config:', err)
    }
  }

  const netCost = config.cost - config.bonus

  const handleCreate = async () => {
    try {
      setIsCreating(true)
      setError(null)

      // 调用批量创建 API，创建 1 个用户
      console.log('=== 创建用户请求 ===')
      console.log('operatorId:', operatorId)
      console.log('count:', 1)
      console.log('initialBalance:', initialBalance)
      
      const response = await apiClient.batchCreateUsers(operatorId, 1, initialBalance)
      
      console.log('=== 创建用户响应 ===')
      console.log('response:', JSON.stringify(response, null, 2))

      if (!response.success) {
        console.error('创建失败:', response.message)
        setError(response.message || '创建用户失败')
        return
      }

      // 获取创建的用户信息
      console.log('response.data:', response.data)
      console.log('response.data?.created_users:', response.data?.created_users)
      
      if (response.data?.created_users && response.data.created_users.length > 0) {
        console.log('✅ 创建成功，设置用户信息:', response.data.created_users[0])
        setCreatedUser(response.data.created_users[0])
        // 不要立即调用 onSuccess()，等用户关闭模态框时再刷新列表
      } else {
        console.error('❌ 未返回用户信息')
        console.error('response.data:', response.data)
        setError('创建用户失败，未返回用户信息')
        return
      }
    } catch (err: any) {
      console.error('创建用户异常:', err)
      setError(err.message || '创建用户失败')
    } finally {
      setIsCreating(false)
    }
  }

  const handleClose = () => {
    // 如果创建了用户，关闭时刷新列表
    if (createdUser) {
      onSuccess()
    }
    setCreatedUser(null)
    setError(null)
    setInitialBalance(0)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">创建用户</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {!createdUser ? (
            <>
              <div className="space-y-4 mb-6">
                {/* 成本和返佣信息卡片 - 简化版 */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600">成本: <span className="font-semibold text-red-600">${config.cost}</span></span>
                      <span className="text-gray-600">返佣: <span className="font-semibold text-green-600">${config.bonus}</span></span>
                      <span className="text-gray-600">净成本: <span className="font-semibold text-purple-600">${netCost}</span></span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    初始余额 (可选)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={initialBalance}
                    onChange={(e) => setInitialBalance(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="输入初始余额"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    用户将获得 {initialBalance} USDT 的初始余额
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-xs text-blue-800">
                    系统将自动生成随机邮箱和密码，创建完成后会显示用户的登录信息，请妥善保存。
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCreate}
                  disabled={isCreating}
                  className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <div className="loading-spinner w-4 h-4 mr-2"></div>
                      创建中...
                    </>
                  ) : (
                    `创建用户（净成本 $${netCost}）`
                  )}
                </button>
                <button
                  onClick={handleClose}
                  disabled={isCreating}
                  className="flex-1 btn btn-secondary"
                >
                  取消
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <CheckCircleIcon className="w-5 h-5 inline mr-2" />
                    用户创建成功！
                  </p>
                </div>

                <div className="border border-gray-200 rounded-md p-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">邮箱</label>
                    <p className="text-sm text-gray-900 font-mono mt-1">{createdUser.email}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">姓名</label>
                    <p className="text-sm text-gray-900 mt-1">{createdUser.full_name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">密码</label>
                    <p className="text-sm text-gray-900 font-mono mt-1">{createdUser.password}</p>
                  </div>
                </div>

                <p className="mt-3 text-xs text-red-600">
                  ⚠️ 请立即复制保存这些登录信息，关闭后将无法再次查看密码！
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const text = `邮箱: ${createdUser.email}\n姓名: ${createdUser.full_name}\n密码: ${createdUser.password}`
                    navigator.clipboard.writeText(text)
                    alert('用户信息已复制到剪贴板')
                  }}
                  className="flex-1 btn btn-primary"
                >
                  复制用户信息
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 btn btn-secondary"
                >
                  关闭
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
